/*
 * promo-gifts.js — configurable free gift engine.
 *
 * Loaded deferred, AFTER bundle.js, so window.Spruce / window.Utils exist.
 * Reads window.STORE.promo_gifts, which sections/promo-gifts.liquid emits.
 * Never modifies bundle.js.
 *
 * Two layers:
 *   1. PromoGifts.compute(cart, rules, options) -> { variantId: desiredQty }
 *      Pure. No DOM, no network, no globals read. Unit-testable on its own.
 *   2. A controller that listens for cart__updated, diffs, and issues at most
 *      one batched cartStore.updateCart() call.
 *
 * Hard rules enforced here:
 *   - A gift variant priced above zero is skipped and warned about once.
 *   - Thresholds come from a per-currency table. Shopify.currency.rate is
 *     never used. A currency absent from the table means the rule is off in
 *     that market.
 *   - Gift lines are subtracted from the cart total before any rule is
 *     evaluated, so a gift can never bootstrap the next tier.
 *   - If nothing differs, no request is issued at all.
 *   - Anything missing or malformed logs once and does nothing.
 */
(function () {
  'use strict';

  var TAG = '[promo-gifts]';
  var warned = {};

  function warnOnce(key, message, detail) {
    if (warned[key]) return;
    warned[key] = true;
    if (arguments.length > 2) {
      console.warn(TAG, message, detail);
    } else {
      console.warn(TAG, message);
    }
  }

  /* ==============================================================
   * Layer 1 — pure evaluation.
   * ============================================================ */

  /*
   * The raw ISO string is authoritative, per the brief. The Liquid-parsed
   * epoch is a fallback so that a date the browser cannot parse (most likely
   * a missing UTC offset) still resolves to the shop-timezone instant the
   * server used, rather than silently dropping the bound.
   */
  function parseInstant(iso, fallbackMs, ruleId, field) {
    if (iso) {
      var t = Date.parse(iso);
      if (!isNaN(t)) return t;
    }
    if (fallbackMs > 0) {
      if (iso) {
        warnOnce('d:' + ruleId + field, 'browser could not parse ' + field + ', using the server value', iso);
      }
      return fallbackMs;
    }
    if (iso) {
      warnOnce('d:' + ruleId + field, 'ignoring unparseable ' + field, iso);
    }
    return null;
  }

  function withinDates(rule, now) {
    var start = parseInstant(rule.starts_at, rule.starts_ts, rule.id, 'start date');
    var end = parseInstant(rule.ends_at, rule.ends_ts, rule.id, 'end date');
    if (start !== null && now < start) return false;
    if (end !== null && now > end) return false;
    return true;
  }

  /*
   * Per-currency table is authoritative when non-empty. No conversion, ever:
   * this store runs two Markets with independent figures, not one converted
   * figure.
   */
  function thresholdCents(rule, currency) {
    var table = rule.currency_thresholds;
    var amount;
    if (table && typeof table === 'object' && Object.keys(table).length > 0) {
      var key = String(currency || '').toUpperCase();
      amount = Object.prototype.hasOwnProperty.call(table, key) ? parseFloat(table[key]) : 0;
    } else {
      amount = parseFloat(rule.threshold);
    }
    return Math.round((amount || 0) * 100);
  }

  function earnedFor(rule, basisCents, currency, now) {
    if (!rule || rule.active !== true) return 0;

    /* Mandatory guard: a gift that costs money must never reach a cart. */
    if ((Number(rule.price) || 0) > 0) {
      warnOnce(
        'p:' + rule.id,
        'gift variant ' + rule.variant_id + ' (' + rule.title + ') is not $0.00 — rule skipped',
        rule.price
      );
      return 0;
    }

    if (!withinDates(rule, now)) return 0;

    var cents = thresholdCents(rule, currency);
    if (!(cents > 0)) return 0;

    var earned = rule.award_type === 'one_time'
      ? (basisCents >= cents ? 1 : 0)
      : Math.floor(basisCents / cents);

    var max = parseInt(rule.max_quantity, 10) || 0;
    if (max > 0 && earned > max) earned = max;

    return earned > 0 ? earned : 0;
  }

  /*
   * Every rule's variant, active or not. A rule that has expired or whose
   * product went over $0 still needs its variant recognised, so its stale
   * gift lines get removed and stop counting toward other rules.
   */
  function giftVariantIds(rules) {
    var ids = {};
    for (var i = 0; i < rules.length; i++) {
      var id = Number(rules[i] && rules[i].variant_id);
      if (id > 0) ids[id] = true;
    }
    return ids;
  }

  function basisCentsFor(cart, ids, useOriginal) {
    var total = (useOriginal ? cart.original_total_price : cart.total_price) || 0;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (!ids[Number(item.variant_id)]) continue;
      total -= (useOriginal ? item.original_line_price : item.final_line_price) || 0;
    }
    return total > 0 ? total : 0;
  }

  function currentQuantities(cart, ids) {
    var current = {};
    for (var i = 0; i < cart.items.length; i++) {
      var id = Number(cart.items[i].variant_id);
      if (!ids[id]) continue;
      current[id] = (current[id] || 0) + (cart.items[i].quantity || 0);
    }
    return current;
  }

  /* (cart, rules) => { variantId: desiredQty } */
  function compute(cart, rules, options) {
    var out = {};
    options = options || {};

    if (!cart || !Array.isArray(cart.items)) {
      warnOnce('nocart', 'cart unavailable — doing nothing');
      return out;
    }
    if (!Array.isArray(rules) || rules.length === 0) return out;

    var currency = options.currency || '';
    var now = typeof options.now === 'number' ? options.now : Date.now();
    var useOriginal = options.basis === 'before';

    var ids = giftVariantIds(rules);
    var basis = basisCentsFor(cart, ids, useOriginal);
    var current = currentQuantities(cart, ids);

    var totalEarned = {};
    var autoEarned = {};
    Object.keys(ids).forEach(function (id) {
      totalEarned[id] = 0;
      autoEarned[id] = 0;
    });

    for (var i = 0; i < rules.length; i++) {
      var rule = rules[i];
      var id = Number(rule && rule.variant_id);
      if (!(id > 0)) continue;
      var earned = earnedFor(rule, basis, currency, now);
      totalEarned[id] += earned;
      if (rule.add_mode !== 'claim') autoEarned[id] += earned;
    }

    Object.keys(ids).forEach(function (id) {
      var total = totalEarned[id];
      var auto = autoEarned[id];
      var inCart = current[id] || 0;
      /*
       * The automatic portion is always applied. The claimable portion is
       * honoured only up to what the customer actually put in the cart. The
       * result can never exceed what was earned, so dropping below a
       * threshold removes claimed gifts too.
       */
      out[id] = Math.max(0, Math.min(total, auto + Math.max(0, inCart - auto)));
    });

    return out;
  }

  /* Returns only what actually differs, or null when nothing does. */
  function diff(cart, desired, ids) {
    if (!cart || !Array.isArray(cart.items)) return null;
    var current = currentQuantities(cart, ids);
    var updates = null;
    var keys = Object.keys(desired);
    for (var i = 0; i < keys.length; i++) {
      var id = keys[i];
      var want = desired[id] || 0;
      var have = current[id] || 0;
      if (want === have) continue;
      if (!updates) updates = {};
      updates[id] = want;
    }
    return updates;
  }

  /* ==============================================================
   * Layer 2 — controller.
   * ============================================================ */

  var inFlight = false;
  var watchdog = null;
  var halted = false;
  var lastSignature = null;
  var repeats = 0;
  var booted = false;

  function config() {
    return (window.STORE && window.STORE.promo_gifts_config) || {};
  }

  function rules() {
    if (!config().enabled) return [];
    var data = window.STORE && window.STORE.promo_gifts;
    if (!Array.isArray(data)) {
      warnOnce('badrules', 'promo_gifts data missing or malformed — doing nothing', data);
      return [];
    }
    return data;
  }

  function options() {
    return {
      currency: (window.STORE && window.STORE.currency_code) || '',
      basis: config().basis || 'after',
      now: Date.now()
    };
  }

  function store() {
    try {
      var s = window.Spruce && window.Spruce.store('cartStore');
      return s && typeof s.updateCart === 'function' ? s : null;
    } catch (e) {
      return null;
    }
  }

  function release() {
    inFlight = false;
    if (watchdog) {
      clearTimeout(watchdog);
      watchdog = null;
    }
  }

  function sync(cart) {
    var ruleset = rules();
    var desired = compute(cart, ruleset, options());

    publish(cart, ruleset, desired);
    lockCartRows(desired);

    if (halted || inFlight) return;

    var updates = diff(cart, desired, giftVariantIds(ruleset));
    if (!updates) return; /* nothing differs -> no request at all */

    /*
     * Convergence guard. If the same batch is sent twice without the cart
     * coming to match, something outside this engine is refusing it — the
     * gift is out of stock, or another app is re-adding it. Stop, rather
     * than loop against the server.
     */
    var signature = JSON.stringify(updates);
    if (signature === lastSignature) {
      repeats++;
    } else {
      lastSignature = signature;
      repeats = 0;
    }
    if (repeats >= 2) {
      halted = true;
      warnOnce('stuck', 'cart is not converging (gift out of stock, or another app is re-adding it) — halting', updates);
      return;
    }

    var cartStore = store();
    if (!cartStore) {
      warnOnce('nostore', 'cartStore unavailable — doing nothing');
      return;
    }

    inFlight = true;
    watchdog = setTimeout(release, 10000);
    /* updateCart, not addToCart: addToCart force-opens the cart tray. */
    cartStore.updateCart(updates);
  }

  function fill(template, tokens) {
    return String(template || '').replace(/\{(\w+)\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(tokens, key) ? tokens[key] : match;
    });
  }

  function money(cents) {
    if (window.Utils && typeof window.Utils.moneyWithoutCurrency === 'function') {
      return window.Utils.moneyWithoutCurrency(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* View models for the tray UI. */
  function describe(cart) {
    var out = [];
    var ruleset = rules();
    if (!cart || !Array.isArray(cart.items) || ruleset.length === 0) return out;

    var opts = options();
    var ids = giftVariantIds(ruleset);
    var basis = basisCentsFor(cart, ids, opts.basis === 'before');
    var current = currentQuantities(cart, ids);

    for (var i = 0; i < ruleset.length; i++) {
      var rule = ruleset[i];
      if (rule.active !== true) continue;
      if ((Number(rule.price) || 0) > 0) continue;
      if (!withinDates(rule, opts.now)) continue;

      var cents = thresholdCents(rule, opts.currency);
      if (!(cents > 0)) continue; /* inactive in this market */

      var earned = earnedFor(rule, basis, opts.currency, opts.now);
      var inCart = current[Number(rule.variant_id)] || 0;
      var max = parseInt(rule.max_quantity, 10) || 0;
      var capped = rule.award_type === 'one_time'
        ? earned > 0
        : (max > 0 && earned >= max);

      var remaining = 0;
      var progress = 100;
      if (!capped) {
        var target = rule.award_type === 'one_time' ? cents : cents * (earned + 1);
        remaining = Math.max(0, target - basis);
        progress = Math.min(100, Math.floor(((cents - remaining) / cents) * 100));
      }

      var tokens = {
        qty: String(earned),
        threshold: money(cents),
        remaining: money(remaining)
      };

      out.push({
        id: rule.id,
        variant_id: Number(rule.variant_id),
        title: rule.title,
        image: rule.image,
        heading: rule.heading,
        message: fill(earned > 0 ? rule.earned_message : rule.progress_message, tokens),
        earned: earned,
        in_cart: inCart,
        is_claim: rule.add_mode === 'claim',
        can_claim: rule.add_mode === 'claim' && earned > inCart,
        progress: progress,
        state: earned > 0 ? 'earned' : 'progress'
      });
    }

    return out;
  }

  function claim(viewModel) {
    var cartStore = store();
    if (!cartStore || !viewModel || !viewModel.can_claim) return;
    var updates = {};
    updates[viewModel.variant_id] = viewModel.earned;
    cartStore.updateCart(updates);
  }

  /*
   * templates/cart.liquid is server-rendered, so there is no Alpine scope to
   * hang an x-show off. Flag its rows instead and let CSS hide the controls.
   */
  function lockCartRows(desired) {
    var rows = document.querySelectorAll('[data-promo-gift-line]');
    for (var i = 0; i < rows.length; i++) {
      var id = rows[i].getAttribute('data-promo-gift-line');
      var isGift = Object.prototype.hasOwnProperty.call(desired, id);
      rows[i].classList.toggle('promo-gifts--locked-line', isGift);
    }
  }

  function publish(cart, ruleset, desired) {
    API.desired = desired;
    API.gift_ids = giftVariantIds(ruleset);
    window.dispatchEvent(new CustomEvent('promo_gifts__updated', { detail: describe(cart) }));
  }

  var API = {
    compute: compute,
    diff: diff,
    describe: describe,
    claim: claim,
    desired: {},
    gift_ids: {},
    isGift: function (variantId) {
      return !!this.gift_ids[Number(variantId)];
    }
  };
  window.PromoGifts = API;

  /* Alpine v2 component factory. Global so x-data can reach it. */
  window.promoGiftsTray = function () {
    return {
      rules: [],
      init: function () {
        var self = this;
        self.refresh(window.STORE && window.STORE.cart);
        window.addEventListener('cart__updated', function (event) {
          self.refresh(event.detail);
        });
      },
      refresh: function (cart) {
        this.rules = API.describe(cart) || [];
      },
      claim: function (rule) {
        API.claim(rule);
      }
    };
  };

  function boot(attempt) {
    if (booted) return;
    if (!config().enabled) return;

    if (!store()) {
      if (attempt >= 20) {
        warnOnce('noboot', 'Spruce cartStore never became available — doing nothing');
        return;
      }
      setTimeout(function () { boot(attempt + 1); }, 100);
      return;
    }

    booted = true;
    window.addEventListener('cart__updated', function (event) {
      release();
      sync(event.detail);
    });
    /* Initial pass: also how a cart left open across an end date gets cleaned. */
    sync((window.STORE && window.STORE.cart) || null);
  }

  try {
    boot(0);
  } catch (error) {
    console.warn(TAG, 'failed to start — doing nothing', error);
  }
})();
